import {Component, AfterViewInit} from '@angular/core';
import {BaseComponent} from '../../../base/base.component';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {GetUnitiesGQL, Unity, User} from '../../../../generated/graphql';
import {UserService} from '../../../service/user.service';
import {DaycareService} from '../../../service/daycare.service';

/**
 * Fatia 5C — CARGA INICIAL (formato legado, §47.6.A). Uso ÚNICO por unidade,
 * TOLERA dados ruins e reporta pendências. Fluxo: escolher unidade + arquivo →
 * PREVIEW (não grava) → confirmar → APPLY (acceptWarnings, descarta row-skip).
 * Só admin e local-manager (§47.2); seletor de unidade só p/ admin.
 */
@Component({
    selector: 'app-daycare-import-legacy',
    templateUrl: './daycare-import-legacy.component.html',
    styleUrls: ['./daycare-import-legacy.component.scss']
})
export class DaycareImportLegacyComponent extends BaseComponent implements AfterViewInit {

    user = new User();
    showUnitySelector = false; // só admin
    unities: Array<Unity> = [];
    unityId: string = null;

    file: File = null;
    fileName = '';
    preview: any = null;
    loading = false;

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private daycareService: DaycareService,
                private getUnitiesGQL: GetUnitiesGQL
    ) {
        super(router, translate);
    }

    ngAfterViewInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
                this.actionUserProfileBTN();
                if (this.actionUserProfile() === false) {
                    super.showMessage(this.translate.instant('atencao'), this.translate.instant('semacesso'), 'warning');
                    this.router.navigate(['/']);
                    return;
                }
                if (this.showUnitySelector) {
                    this.loadUnities();
                }
            }, error: err => super.onError(err)
        });
    }

    loadUnities() {
        this.getUnitiesGQL.watch().valueChanges.subscribe(({data}) => {
            this.unities = (data.unities || []) as Array<Unity>;
        }, error => super.onError(error));
    }

    onFileSelected(event) {
        const files = event.target.files;
        this.file = files && files.length ? files[0] : null;
        this.fileName = this.file ? this.file.name : '';
        this.preview = null; // trocou o arquivo → invalida o preview anterior
    }

    // Admin precisa escolher a unidade; local-manager é forçado à dele (unityId null).
    private effectiveUnityId(): string {
        return this.showUnitySelector ? this.unityId : null;
    }

    analyze() {
        if (this.showUnitySelector && !this.unityId) {
            super.showMessage(this.translate.instant('atencao'), this.translate.instant('daycare.selecioneunidade'), 'warning');
            return;
        }
        if (!this.file) {
            super.showMessage(this.translate.instant('atencao'), this.translate.instant('daycare.selecionearquivo'), 'warning');
            return;
        }
        this.loading = true;
        this.preview = null;
        this.daycareService.importLegacyPreview(this.file, this.effectiveUnityId()).subscribe({
            next: (data: any) => {
                this.preview = data;
                this.loading = false;
            }, error: (err) => {
                this.loading = false;
                this.handleRestError(err);
            }
        });
    }

    pendenciesBySeverity(severity: string): Array<any> {
        if (!this.preview || !this.preview.pendencies) {
            return [];
        }
        return this.preview.pendencies.filter((p) => p.severity === severity);
    }

    confirmImport() {
        if (!this.preview || this.preview.blocked || !this.preview.canProceed) {
            return;
        }
        const t = this.preview.totals || {};
        const skipped = (this.preview.rowsSkipped || []).length;
        let msg = this.translate.instant('daycare.resumoimportar')
            .replace('{emp}', t.employees || 0)
            .replace('{chi}', t.children || 0)
            .replace('{gua}', t.guardians || 0);
        if (skipped > 0) {
            msg += '<br><br><b class="text-danger">' +
                this.translate.instant('daycare.resumodescarte').replace('{n}', skipped) + '</b>';
        }
        super.confirmMessage(this.translate.instant('atencao'), msg, () => this.apply());
    }

    private apply() {
        this.loading = true;
        this.daycareService.importLegacyApply(this.file, this.effectiveUnityId()).subscribe({
            next: (data: any) => {
                this.loading = false;
                if (data && data.blocked) {
                    super.showMessage(this.translate.instant('atencao'),
                        this.translate.instant('daycare.unidadejacarregada'), 'warning');
                    return;
                }
                if (data && data.applied) {
                    const c = data.created || {};
                    const msg = this.translate.instant('daycare.importadosucesso')
                        .replace('{emp}', c.employees || 0)
                        .replace('{chi}', c.children || 0)
                        .replace('{gua}', c.guardians || 0);
                    super.showMessage(this.translate.instant('success'), msg, 'success');
                    this.router.navigate(['/daycare']);
                    return;
                }
                // requiresConfirmation não deveria ocorrer (mandamos acceptWarnings=true).
                super.showMessage(this.translate.instant('atencao'),
                    this.translate.instant('daycare.naogravado'), 'warning');
            }, error: (err) => {
                this.loading = false;
                this.handleRestError(err);
            }
        });
    }

    // Erro do XHR cru já normalizado em {status, body} pelo DaycareService.
    private handleRestError(err: any) {
        if (err && err.status === 401) {
            this.router.navigate(['/login']);
            return;
        }
        const body = err && err.body ? err.body : err;
        const message = (body && body.message) ? body.message : this.translate.instant('daycare.erroimportacao');
        super.showMessage(this.translate.instant('atencao'), message, 'warning');
    }

    actionUserProfile(): boolean {
        switch (this.user.profile) {
            case 'admin':
                return true;
            case 'local-manager':
                return true;
            default:
                return false;
        }
    }

    actionUserProfileBTN() {
        this.showUnitySelector = this.user.profile === 'admin';
    }
}
